import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Bed, Bath, Ruler, Fuel, Calendar, Gauge, Cog, FileText, Phone, MessageCircle, Heart, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { getOfferLabel, getPricePeriodLabel, SPACE_CATEGORY_LABELS } from "@/lib/offer";
import api, { resolveMediaUrl } from "@/lib/api";
import BookingDialog from "@/components/BookingDialog";
import StayBookingDialog from "@/components/StayBookingDialog";
import { trackDownload } from "@/components/AnalyticsTracker";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { CATEGORY_LABELS, SPEC_LABELS, formatSpecValue } from "@/lib/catalog";
import { toTelHref, toWhatsAppHref, visibleContactItems } from "@/lib/contactSettings";
import { getListingSchema, publicSpecs } from "@/lib/listingSchemaRegistry";

export default function ListingDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { has, toggle } = useFavorites();
  const { settings } = useSiteSettings();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setListing(null));
  }, [id]);

  useEffect(() => {
    if (!listing) return undefined;
    const pageTitle = `${listing.title} | Team Work Kinshasa`;
    const canonical = `https://teamwork-africa.com/annonce/${listing.id}`;
    document.title = pageTitle;
    const descriptionText = (listing.description || `${listing.title} disponible à ${listing.city || "Kinshasa"}.`).slice(0, 160);
    const setMeta = (selector, attribute, value) => {
      let node = document.head.querySelector(selector);
      if (!node) { node = document.createElement("meta"); document.head.appendChild(node); }
      node.setAttribute(attribute, value);
    };
    setMeta('meta[name="description"]', "content", descriptionText);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", descriptionText);
    setMeta('meta[property="og:url"]', "content", canonical);
    if (listing.images?.[0]) setMeta('meta[property="og:image"]', "content", resolveMediaUrl(listing.images[0]));
    const link = document.head.querySelector('link[rel="canonical"]');
    if (link) link.href = canonical;
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "teamwork-listing-schema";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": listing.sector === "auto" ? "Vehicle" : "Accommodation",
      name: listing.title, description: descriptionText, image: (listing.images || []).map(resolveMediaUrl),
      url: canonical, address: { "@type": "PostalAddress", addressLocality: listing.city, addressCountry: listing.country || "CD" },
      offers: { "@type": "Offer", price: listing.price, priceCurrency: listing.currency || "USD", availability: ["sold", "rented"].includes(listing.status) ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" },
    });
    document.head.appendChild(schema);
    return () => schema.remove();
  }, [listing]);

  if (!listing) {
    return <div className="container-app py-24 text-center text-slate-500">Chargement…</div>;
  }

  const isAuto = listing.sector === "auto";
  const isStay = listing.sector === "hospitality";
  const offerLabel = getOfferLabel(listing, lang);
  const periodLabel = getPricePeriodLabel(listing.price_period, lang);
  const isBookableSpace = Boolean(SPACE_CATEGORY_LABELS[listing.category]);
  const isClosed = ["sold", "rented"].includes(listing.status);
  const listingSchema = getListingSchema(listing);
  const visibleSpecs = publicSpecs(listing);
  const actionType = listingSchema.cta;
  const backHref = isAuto ? "/explorer?sector=auto" : isStay ? "/explorer?sector=hospitality" : isBookableSpace ? "/univers/espaces" : "/explorer?sector=real_estate";
  const contextualAction = ({investment_file:"Demander le dossier d’investissement",availability:"Vérifier les disponibilités",dates:"Choisir mes dates",quote:"Demander un devis",quote_date:"Vérifier la date et demander un devis",meeting:"Organiser une rencontre",contact:isAuto?"Demander les informations du véhicule":"Contacter Team Work",visit:isAuto?"Réserver un essai":"Réserver une visite"})[actionType] || "Faire une demande";
  const surface = listing.specs?.surface_m2 ?? listing.specs?.surface_habitable_m2 ?? listing.specs?.surface_terrain_m2;
  const phone = visibleContactItems(settings?.phones, settings?.phone, "Téléphone")[0]?.value || "";
  const whatsapp = visibleContactItems(settings?.whatsapps, settings?.whatsapp, "WhatsApp")[0]?.value || "";
  const title = lang === "en" && listing.title_en ? listing.title_en : listing.title;
  const description = lang === "en" && listing.description_en ? listing.description_en : listing.description;
  const price = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US", {
    style: "currency", currency: listing.currency, maximumFractionDigits: 0,
  }).format(listing.price || 0);
  const images = (Array.isArray(listing.images) ? listing.images : []).map(resolveMediaUrl).filter(Boolean);
  const videos = (Array.isArray(listing.videos) ? listing.videos : []).map(resolveMediaUrl).filter(Boolean);
  const hasImages = images.length > 0;
  const fav = has(listing.id);
  const whatsappBase = toWhatsAppHref(whatsapp);
  const wa = whatsappBase ? `${whatsappBase}?text=${encodeURIComponent(`Bonjour Team Work, je suis intéressé par « ${title} » (${window.location.href})`)}` : "";
  const phoneHref = toTelHref(phone);

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title, text: title, url: window.location.href });
      else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Lien copié");
      }
    } catch {}
  };

  return (
    <div className="pb-32 lg:pb-16">
      {/* Back bar */}
      <div className="sticky top-0 lg:top-20 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="container-app py-2.5 flex items-center justify-between">
          <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-brand-orange" data-testid="back-listings">
            <ArrowLeft className="h-4 w-4" /> {t("listing.backList")}
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={share} className="h-9 w-9 rounded-full bg-brand-mist flex items-center justify-center text-brand-navy hover:bg-slate-200" data-testid="share-btn" aria-label="Partager">
              <Share2 className="h-4 w-4" />
            </button>
            <button onClick={() => toggle(listing.id)} className={`h-9 w-9 rounded-full flex items-center justify-center ${fav ? "bg-brand-orangeSoft text-brand-orange" : "bg-brand-mist text-brand-navy"}`} data-testid="detail-fav" aria-label="Favori">
              <Heart className="h-4 w-4" fill={fav ? "currentColor" : "none"} />
            </button>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <section className="container-app pt-3">
        {hasImages ? (
          <>
            <div className="rounded-2xl overflow-hidden bg-brand-mist aspect-[4/3] lg:aspect-[16/9] relative">
              <img src={images[active]} alt={title} className="w-full h-full object-cover" data-testid="listing-hero-image" />
              <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                <StatusBadge status={listing.status} lang={lang} />
                {listing.featured && <StatusBadge status="featured" lang={lang} />}
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-2.5 flex gap-2 overflow-x-auto no-scrollbar">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${i === active ? "border-brand-orange" : "border-transparent"}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="rounded-2xl bg-brand-mist aspect-[4/3] lg:aspect-[16/9] flex items-center justify-center">
            <EmptyState icon={FileText} title={t("listing.noMedia")} />
          </div>
        )}
      </section>

      {/* Content */}
      <section className="container-app py-5 lg:py-8 lg:grid lg:grid-cols-3 lg:gap-10">
        <div className="lg:col-span-2">
          {/* Title block */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-orange">{offerLabel}</div>
            <h1 className="mt-1.5 font-display text-2xl lg:text-4xl font-bold text-brand-navy leading-tight" data-testid="listing-title">
              {title}
            </h1>
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="h-3.5 w-3.5" />
              <span>{listing.city}{listing.zone ? ` · ${listing.zone}` : ""} · {listing.country}</span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-3xl lg:text-4xl font-extrabold text-brand-navy" data-testid="listing-price">{price}</div>
              {listing.purpose === "rent" && <div className="text-sm text-slate-500">{periodLabel}</div>}
            </div>
          </div>

          {/* Specs quick strip */}
          <div className="mt-5 flex flex-wrap gap-2">
            {!isAuto && listing.specs?.chambres != null && <span className="chip"><Bed className="h-3.5 w-3.5" /> {listing.specs.chambres} chambres</span>}
            {!isAuto && listing.specs?.sdb != null && <span className="chip"><Bath className="h-3.5 w-3.5" /> {listing.specs.sdb} SDB</span>}
            {surface != null && surface !== "" && <span className="chip"><Ruler className="h-3.5 w-3.5" /> {surface} m²</span>}
            {!isAuto && listing.specs?.capacite_personnes && <span className="chip">{listing.specs.capacite_personnes} personnes</span>}
            {isAuto && listing.specs?.annee != null && <span className="chip"><Calendar className="h-3.5 w-3.5" /> {listing.specs.annee}</span>}
            {isAuto && listing.specs?.km != null && <span className="chip"><Gauge className="h-3.5 w-3.5" /> {listing.specs.km.toLocaleString()} km</span>}
            {isAuto && listing.specs?.carburant && <span className="chip"><Fuel className="h-3.5 w-3.5" /> {listing.specs.carburant}</span>}
            {isAuto && listing.specs?.transmission && <span className="chip"><Cog className="h-3.5 w-3.5" /> {listing.specs.transmission}</span>}
          </div>

          {/* Description */}
          {description && (
            <section className="mt-8">
              <h2 className="font-display text-lg lg:text-2xl font-bold text-brand-navy">{t("listing.description")}</h2>
              <p className="mt-3 text-sm lg:text-base text-brand-ink/80 leading-relaxed whitespace-pre-line">{description}</p>
            </section>
          )}

          {/* Specs full */}
          {visibleSpecs.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-lg lg:text-2xl font-bold text-brand-navy">{t("listing.specs")}</h2>
              <dl className="mt-3 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                {visibleSpecs.map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl bg-brand-mist">
                    <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{SPEC_LABELS[k] || k.replaceAll("_", " ")}</dt>
                    <dd className="mt-1 font-display text-base font-bold text-brand-navy">{formatSpecValue(k, v)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {videos.length > 0 && <section className="mt-8"><h2 className="font-display text-lg lg:text-2xl font-bold text-brand-navy">Vidéos</h2><div className="mt-3 grid gap-4">{videos.map((video, i) => <video key={video} src={video} controls preload="metadata" className="w-full rounded-2xl bg-black" aria-label={`Vidéo ${i + 1} de ${title}`} />)}</div></section>}

          {/* Documents */}
          <section className="mt-8">
            <h2 className="font-display text-lg lg:text-2xl font-bold text-brand-navy">{t("listing.documents")}</h2>
            {listing.documents?.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {listing.documents.map((d, i) => (
                  <li key={i}>
                    <a href={d.url} target="_blank" rel="noreferrer" onClick={() => trackDownload({ item_id: listing.id, label: d.name || "Document annonce" })} className="flex items-center gap-2 p-3 rounded-xl bg-brand-mist text-brand-navy hover:bg-slate-200 transition-colors">
                      <FileText className="h-4 w-4 text-brand-orange" />
                      <span className="text-sm font-semibold">{d.name || d.url}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-slate-500">{t("listing.noDocs")}</p>
            )}
          </section>
        </div>

        {/* Desktop sticky sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-32 card-app p-6">
            <div className="kicker">{listing.purpose === "sale" ? t("sectors.sale") : t("sectors.rent")} · {CATEGORY_LABELS[listing.category] || listing.category}</div>
            <div className="mt-3 font-display text-3xl font-extrabold text-brand-navy">{price}</div>
            {isClosed ? (
              <div className={`mt-5 rounded-xl px-4 py-3 text-center text-sm font-bold text-white ${listing.status === "sold" ? "bg-red-600" : "bg-violet-600"}`} data-testid="listing-closed-desktop">
                {listing.status === "sold" ? "Offre vendue — aucune visite disponible" : "Offre louée — aucune visite disponible"}
              </div>
            ) : (
              <Button onClick={() => setOpen(true)} className="btn-primary w-full mt-5" data-testid="btn-book-visit-desktop">
                {contextualAction}
              </Button>
            )}
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer" className="btn-navy w-full mt-3 no-underline">
                <MessageCircle className="h-4 w-4" /> {t("listing.whatsapp")}
              </a>
            )}
            {phone ? (
              <a href={phoneHref} className="btn-secondary w-full mt-3 no-underline">
                <Phone className="h-4 w-4" /> {t("listing.contact")}
              </a>
            ) : (
              <Link to="/contact" className="btn-secondary w-full mt-3 no-underline">
                <Phone className="h-4 w-4" /> {t("listing.contact")}
              </Link>
            )}
            <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500">
              <p>Bon de visite émis instantanément. Aucun compte requis.</p>
            </div>
          </div>
        </aside>
      </section>

      {/* Mobile sticky action bar */}
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-slate-100 bg-white/95 pb-safe shadow-[0_-14px_40px_rgba(15,23,42,.14)] backdrop-blur-xl lg:hidden">
        <div className="container-app space-y-2 py-2.5">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[9px] font-extrabold uppercase tracking-[.18em] text-slate-400">{t("listing.price")}</div>
              <div className="truncate font-display text-lg font-extrabold text-brand-navy">{price}</div>
            </div>
            {wa ? <a href={wa} target="_blank" rel="noreferrer" className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-600" aria-label="WhatsApp" data-testid="sticky-wa"><MessageCircle className="h-4.5 w-4.5" /></a> : <Link to="/contact" className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-mist text-brand-navy" aria-label="Contacter Team Work" data-testid="sticky-contact"><Phone className="h-4.5 w-4.5" /></Link>}
            {phoneHref && <a href={phoneHref} className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-mist text-brand-navy" aria-label="Appeler Team Work" data-testid="sticky-phone"><Phone className="h-4.5 w-4.5" /></a>}
          </div>
          {isClosed ? (
            <div className={`flex h-12 w-full items-center justify-center rounded-2xl px-4 text-sm font-extrabold text-white ${listing.status === "sold" ? "bg-red-600" : "bg-violet-600"}`} data-testid="listing-closed-mobile">
              {listing.status === "sold" ? "Cette offre est vendue" : "Cette offre est louée"}
            </div>
          ) : (
            <Button onClick={() => setOpen(true)} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-brand-orange to-orange-500 text-sm font-extrabold text-white shadow-[0_10px_24px_rgba(254,108,9,.28)]" data-testid="btn-book-visit">
              {["quote","quote_date","investment_file"].includes(actionType) ? <FileText className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
              {contextualAction}
            </Button>
          )}
        </div>
      </div>

      {!isClosed && (isStay ? <StayBookingDialog open={open} onOpenChange={setOpen} listing={listing} /> : <BookingDialog open={open} onOpenChange={setOpen} listing={listing} cta={actionType} actionLabel={contextualAction} />)}
    </div>
  );
}
